/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';

import { ElementType } from 'react';

import { TitleSectionProps, TitleAttributes } from './types';
import {
  DEFAULT_WRAPPER_PROPS,
  DEFAULT_TITLE_COMPONENT,
  createCommandBarStyles,
  createDescriptionStyles,
  getDefaultFontSize,
  getDefaultFontWeight,
} from './styles';

/**
 * Creates title attributes by combining theme values, CommandBar data, and custom props
 *
 * @param {object} params - Parameters for creating title attributes
 * @returns {TitleAttributes} Combined title attributes object
 */
const createTitleAttributes = ({
  theme,
  fontSize,
  fontWeight,
  color,
  commandBarHook,
  title,
  action,
  titleProps,
}: {
  theme: any;
  fontSize?: number | string;
  fontWeight?: number | string;
  color?: string;
  commandBarHook?: any;
  title: string;
  action?: string;
  component: ElementType;
  titleProps: any;
}): TitleAttributes => {
  // Use theme values as defaults, but allow override via props
  const defaultFontSize = getDefaultFontSize(theme, fontSize);
  const defaultFontWeight = getDefaultFontWeight(theme, fontWeight);

  // Generate CommandBar attributes if hook is provided and title exists
  const commandBarAttributes = commandBarHook && title ? commandBarHook.createTitleAttribute(title, action) : {};

  // Apply CommandBar-specific styles when CommandBar is enabled
  const commandBarStyles = createCommandBarStyles(!!(commandBarHook && title));

  // Combine all title attributes and styles exactly like the original JS implementation
  return {
    fontSize: defaultFontSize,
    fontWeight: defaultFontWeight,
    ...(color && { color }),
    ...commandBarAttributes, // Includes data-commandbar-id
    ...commandBarStyles, // Includes display: inline-block, width: fit-content when CommandBar is enabled
    ...titleProps, // Custom props override defaults
  };
};

/**
 * Component for displaying section titles with optional descriptions and CommandBar integration
 *
 * @param {TitleSectionProps} props - Component properties
 * @returns {JSX.Element} TitleSection component
 *
 * @example
 * // Basic usage
 * <TitleSection title="Sales Report" />
 *
 * // With description
 * <TitleSection
 *   title="Sales Report"
 *   description="Monthly sales analysis"
 * />
 *
 * // With CommandBar integration
 * <TitleSection
 *   title="Sales Report"
 *   commandBarHook={useCommandBarReports()}
 * />
 *
 * // With action for CommandBar (generates: section-title-action)
 * <TitleSection
 *   title="Product Management"
 *   action="agregar"
 *   commandBarHook={useCommandBarReports()}
 * />
 */
const TitleSection = ({
  title,
  description = '',
  action,
  commandBarHook,
  wrapperProps = DEFAULT_WRAPPER_PROPS,
  fontSize,
  fontWeight,
  color,
  component = DEFAULT_TITLE_COMPONENT,
  titleProps = {},
  descriptionProps = {},
}: TitleSectionProps) => {
  const theme = useTheme();

  // Create title attributes using separated utility function
  const titleAttributes = createTitleAttributes({
    theme,
    fontSize,
    fontWeight,
    color,
    commandBarHook,
    title,
    action,
    component,
    titleProps,
  });

  // Create description styles using separated utility function
  const descriptionStyles = createDescriptionStyles(theme);

  return (
    <Box {...wrapperProps}>
      <Box component={component} {...titleAttributes}>
        {title}
      </Box>
      {description && (
        <Box sx={descriptionStyles} {...descriptionProps}>
          {description}
        </Box>
      )}
    </Box>
  );
};

export default TitleSection;
